import React, { useState } from "react";
import styles from "../../styles/resumeBuilder.module.css";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ResumeBuilder } from "../../lib/ResumeBuilder";

const getBulletWidthInfo = (text, config) => {
  if (!text || !config) return { percentage: 0, suggestion: "" };
  try {
    const tempBuilder = new ResumeBuilder(config);
    tempBuilder.setFont("normal");
    const availableWidth =
      tempBuilder.contentWidth - (tempBuilder.config.spacing.indentation || 5);
    const textWidth = tempBuilder.doc.getTextWidth(text.trim());
    const percentage = Math.round((textWidth / availableWidth) * 100);
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    const avgWidthPerWord = words > 0 ? textWidth / words : 10;
    let suggestion = "";
    if (percentage > 100) {
      const excessWidth = textWidth - availableWidth;
      const wordsToRemove = Math.ceil(excessWidth / avgWidthPerWord);
      suggestion = `(remove ~${wordsToRemove} ${
        wordsToRemove > 1 ? "words" : "word"
      })`;
    } else if (percentage < 90 && percentage > 0) {
      const neededWidth = availableWidth * 0.95 - textWidth;
      const wordsToAdd = Math.round(neededWidth / avgWidthPerWord);
      if (wordsToAdd > 0)
        suggestion = `(add ~${wordsToAdd} ${
          wordsToAdd > 1 ? "words" : "word"
        })`;
    }
    return { percentage, suggestion };
  } catch (e) {
    console.error("Error calculating bullet width:", e);
    return { percentage: 0, suggestion: "" };
  }
};

const FormEntry = ({ type, entry, index, handlers, config }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: entry.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [isExpanded, setIsExpanded] = useState(false);
  const entryTitle =
    entry.school ||
    entry.company ||
    entry.organization ||
    entry.title ||
    `Entry ${index + 1}`;

  const renderFields = () => {
    const commonFields = (
      <>
        <div className={styles.inputRow}>
          <div className={styles.formGroup}>
            <label>Duration</label>
            <input
              type="text"
              name="duration"
              value={entry.duration || ""}
              onChange={(e) => handlers.handleChange(type, index, e)}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={entry.location || ""}
              onChange={(e) => handlers.handleChange(type, index, e)}
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>Description</label>
          <textarea
            name="description"
            value={entry.description || ""}
            onChange={(e) => handlers.handleChange(type, index, e)}
            rows={2}
          />
        </div>
        <div className={styles.bulletSection}>
          <label>Bullet Points (one per line)</label>
          {(entry.bullets || []).map((bullet, bIndex) => {
            const { percentage, suggestion } = getBulletWidthInfo(
              bullet,
              config
            );
            return (
              <div key={bIndex} className={styles.bulletInputWrapper}>
                <input
                  type="text"
                  value={bullet}
                  onChange={(e) =>
                    handlers.handleBulletChange(type, index, bIndex, e)
                  }
                />
                <span
                  className={styles.bulletWidthInfo}
                  style={{
                    color:
                      percentage > 100
                        ? "#ef4444"
                        : percentage < 90
                        ? "#f59e0b"
                        : "#10b981",
                  }}
                >
                  {percentage > 0 ? `${percentage}% ${suggestion}` : ""}
                </span>
                <button
                  onClick={() => handlers.removeBullet(type, index, bIndex)}
                  className={styles.removeSmallBtn}
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            onClick={() => handlers.addBullet(type, index)}
            className={styles.addBulletBtn}
          >
            Add Bullet Point
          </button>
        </div>
        <div className={styles.formGroup}>
          <label>Technologies Used (comma-separated)</label>
          <input
            type="text"
            name="tags"
            value={
              Array.isArray(entry.tags)
                ? entry.tags.join(", ")
                : entry.tags || ""
            }
            onChange={(e) => handlers.handleChange(type, index, e)}
          />
        </div>
      </>
    );

    switch (type) {
      case "education":
        return (
          <>
            <div className={styles.formGroup}>
              <label>School/University</label>
              <input
                type="text"
                name="school"
                value={entry.school || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Degree</label>
              <input
                type="text"
                name="degree"
                value={entry.degree || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.inputRow}>
              <div className={styles.formGroup}>
                <label>Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={entry.duration || ""}
                  onChange={(e) => handlers.handleChange(type, index, e)}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={entry.location || ""}
                  onChange={(e) => handlers.handleChange(type, index, e)}
                />
              </div>
            </div>
            <div className={styles.inputRow}>
              <div className={styles.formGroup}>
                <label>Score Type</label>
                <input
                  type="text"
                  name="scoreType"
                  value={entry.scoreType || ""}
                  onChange={(e) => handlers.handleChange(type, index, e)}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Score</label>
                <input
                  type="text"
                  name="gpa"
                  value={entry.gpa || ""}
                  onChange={(e) => handlers.handleChange(type, index, e)}
                />
              </div>
            </div>
          </>
        );
      case "experience":
        return (
          <>
            <div className={styles.formGroup}>
              <label>Company</label>
              <input
                type="text"
                name="company"
                value={entry.company || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Position</label>
              <input
                type="text"
                name="title"
                value={entry.title || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            {commonFields}
          </>
        );
      case "leadership":
        return (
          <>
            <div className={styles.formGroup}>
              <label>Organization</label>
              <input
                type="text"
                name="organization"
                value={entry.organization || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Role</label>
              <input
                type="text"
                name="title"
                value={entry.title || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            {commonFields}
          </>
        );
      case "projects":
      default:
        return (
          <>
            <div className={styles.formGroup}>
              <label>Title</label>
              <input
                type="text"
                name="title"
                value={entry.title || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Subtitle</label>
              <input
                type="text"
                name="subtitle"
                value={entry.subtitle || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            {commonFields}
          </>
        );
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.formEntry} ${isExpanded ? styles.isOpen : ""}`}
    >
      <div
        className={styles.entryHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span {...listeners} {...attributes} className={styles.entryDragHandle}>
          <i className="fas fa-grip-lines"></i>
        </span>
        <span className={styles.entryTitle}>{entryTitle}</span>
        <div
          className={`${styles.toggleIcon} ${isExpanded ? styles.isOpen : ""}`}
        />
      </div>
      {isExpanded && (
        <div className={styles.entryContent}>
          {renderFields()}
          <div className={styles.entryActions}>
            <button
              onClick={() => handlers.removeEntry(type, index)}
              className={styles.removeButton}
            >
              <i className="fas fa-trash-alt"></i> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ type, resumeData, handlers, config }) => {
  const [isCollapsed, setIsCollapsed] = useState(type !== "personal");
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: type });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const title = type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " ");
  const sectionData = resumeData[type] || [];

  const handleEntryDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id)
      handlers.reorderEntries(type, active.id, over.id);
  };

  const SortableSkillCategory = ({ entry, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: entry.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const [isSkillExpanded, setIsSkillExpanded] = useState(true);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${styles.formEntry} ${
          isSkillExpanded ? styles.isOpen : ""
        }`}
      >
        <div
          className={styles.entryHeader}
          onClick={() => setIsSkillExpanded(!isSkillExpanded)}
        >
          <span
            {...listeners}
            {...attributes}
            className={styles.entryDragHandle}
          >
            <i className="fas fa-grip-lines"></i>
          </span>
          <span className={styles.entryTitle}>
            {entry.category || `Category ${index + 1}`}
          </span>
          <div
            className={`${styles.toggleIcon} ${
              isSkillExpanded ? styles.isOpen : ""
            }`}
          />
        </div>
        {isSkillExpanded && (
          <div className={styles.entryContent}>
            <div className={styles.formGroup}>
              <label>Category Name</label>
              <input
                type="text"
                name="category"
                value={entry.category || ""}
                onChange={(e) => handlers.handleChange(type, index, e)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Skills</label>
              <div className={styles.skillsGrid}>
                {(entry.skills || []).map((skill, skillIndex) => (
                  <div key={skillIndex} className={styles.skillInputWrapper}>
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) =>
                        handlers.handleSkillChange(
                          index,
                          skillIndex,
                          e.target.value
                        )
                      }
                    />
                    <button
                      onClick={() => handlers.removeSkill(index, skillIndex)}
                      className={styles.removeSkillBtn}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.entryActions}>
              <button
                onClick={() => handlers.addSkill(index)}
                className={styles.addSkillBtn}
              >
                <i className="fas fa-plus"></i> Add Skill
              </button>
              <button
                onClick={() => handlers.removeEntry(type, index)}
                className={styles.removeButton}
              >
                <i className="fas fa-trash-alt"></i> Remove
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.sectionGroup} ${!isCollapsed ? styles.isOpen : ""}`}
    >
      <div
        className={styles.groupHeader}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span {...attributes} {...listeners} className={styles.dragHandle}>
          <i className="fas fa-grip-lines"></i>
        </span>
        <h3>{title}</h3>
        <div
          className={`${styles.toggleIcon} ${
            !isCollapsed ? styles.isOpen : ""
          }`}
        />
      </div>
      {!isCollapsed && (
        <div className={styles.groupContent}>
          {["personal", "summary"].includes(type) &&
            sectionData.map((entry, index) => (
              <div key={entry.id || index} className={styles.entryContent}>
                {type === "personal" && (
                  <>
                    <div className={styles.formGroup}>
                      <label>Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={entry.name || ""}
                        onChange={(e) => handlers.handleChange(type, index, e)}
                      />
                    </div>
                    <div className={styles.inputRow}>
                      <div className={styles.formGroup}>
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={entry.email || ""}
                          onChange={(e) =>
                            handlers.handleChange(type, index, e)
                          }
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={entry.phone || ""}
                          onChange={(e) =>
                            handlers.handleChange(type, index, e)
                          }
                        />
                      </div>
                    </div>
                    <div className={styles.inputRow}>
                      <div className={styles.formGroup}>
                        <label>LinkedIn</label>
                        <input
                          type="text"
                          name="linkedin"
                          value={entry.linkedin || ""}
                          onChange={(e) =>
                            handlers.handleChange(type, index, e)
                          }
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label>GitHub</label>
                        <input
                          type="text"
                          name="github"
                          value={entry.github || ""}
                          onChange={(e) =>
                            handlers.handleChange(type, index, e)
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
                {type === "summary" && (
                  <div className={styles.formGroup}>
                    <label>Professional Summary</label>
                    <textarea
                      name="summaryText"
                      rows={5}
                      value={entry.summaryText || ""}
                      onChange={(e) => handlers.handleChange(type, index, e)}
                    />
                  </div>
                )}
              </div>
            ))}

          {type === "skills" && (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleEntryDragEnd}
            >
              <SortableContext
                items={sectionData.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {sectionData.map((entry, index) => (
                  <SortableSkillCategory
                    key={entry.id}
                    entry={entry}
                    index={index}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {!["personal", "summary", "skills"].includes(type) && (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleEntryDragEnd}
            >
              <SortableContext
                items={sectionData.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {sectionData.map((entry, index) => (
                  <FormEntry
                    key={entry.id}
                    type={type}
                    entry={entry}
                    index={index}
                    handlers={handlers}
                    config={config}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {!["personal", "summary"].includes(type) && (
            <div className={styles.groupActions}>
              <button
                onClick={() => handlers.addEntry(type)}
                className={styles.addButton}
              >
                <i className="fas fa-plus"></i> Add New
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResumeForm = ({
  resumeData,
  setResumeData,
  sectionOrder,
  setSectionOrder,
  config,
}) => {
  const handlers = {
    handleChange: (section, index, event) => {
      const { name, value } = event.target;
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData[section][index][name] = value;
        return newData;
      });
    },
    handleBulletChange: (section, entryIndex, bulletIndex, event) => {
      const { value } = event.target;
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData[section][entryIndex].bullets[bulletIndex] = value;
        return newData;
      });
    },
    handleSkillChange: (entryIndex, skillIndex, value) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        if (!newData.skills[entryIndex].skills)
          newData.skills[entryIndex].skills = [];
        newData.skills[entryIndex].skills[skillIndex] = value;
        return newData;
      });
    },
    addSkill: (entryIndex) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        if (!newData.skills[entryIndex].skills)
          newData.skills[entryIndex].skills = [];
        newData.skills[entryIndex].skills.push("");
        return newData;
      });
    },
    removeSkill: (entryIndex, skillIndex) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData.skills[entryIndex].skills.splice(skillIndex, 1);
        return newData;
      });
    },
    toggleSkillExpansion: (index) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData.skills[index].isExpanded = !newData.skills[index].isExpanded;
        return newData;
      });
    },
    addEntry: (section) => {
      let newEntry = { id: `${section}-${Date.now()}` };
      switch (section) {
        case "education":
          newEntry = {
            ...newEntry,
            school: "",
            degree: "",
            duration: "",
            location: "",
            scoreType: "",
            gpa: "",
          };
          break;
        case "experience":
          newEntry = {
            ...newEntry,
            company: "",
            title: "",
            duration: "",
            location: "",
            description: "",
            bullets: [""],
            tags: "",
          };
          break;
        case "leadership":
          newEntry = {
            ...newEntry,
            organization: "",
            title: "",
            duration: "",
            location: "",
            description: "",
            bullets: [""],
            tags: "",
          };
          break;
        case "skills":
          newEntry = {
            ...newEntry,
            category: "",
            skills: [""],
            isExpanded: true,
          };
          break;
        default:
          newEntry = {
            ...newEntry,
            title: "",
            subtitle: "",
            duration: "",
            location: "",
            description: "",
            bullets: [""],
            tags: "",
          };
          break;
      }
      setResumeData((prev) => ({
        ...prev,
        [section]: [...(prev[section] || []), newEntry],
      }));
    },
    removeEntry: (section, index) => {
      setResumeData((prev) => {
        const list = [...prev[section]];
        list.splice(index, 1);
        return { ...prev, [section]: list };
      });
    },
    addBullet: (section, entryIndex) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        if (!newData[section][entryIndex].bullets)
          newData[section][entryIndex].bullets = [];
        newData[section][entryIndex].bullets.push("");
        return newData;
      });
    },
    removeBullet: (section, entryIndex, bulletIndex) => {
      setResumeData((prev) => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData[section][entryIndex].bullets.splice(bulletIndex, 1);
        return newData;
      });
    },
    reorderEntries: (section, activeId, overId) => {
      setResumeData((prev) => {
        const list = prev[section];
        const oldIndex = list.findIndex((item) => item.id === activeId);
        const newIndex = list.findIndex((item) => item.id === overId);
        return { ...prev, [section]: arrayMove(list, oldIndex, newIndex) };
      });
    },
  };

  function handleGroupDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = sectionOrder.indexOf(active.id);
      const newIndex = sectionOrder.indexOf(over.id);
      setSectionOrder(arrayMove(sectionOrder, oldIndex, newIndex));
    }
  }

  return (
    <div className={styles.formContainer}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleGroupDragEnd}
      >
        <SortableContext
          items={sectionOrder}
          strategy={verticalListSortingStrategy}
        >
          {sectionOrder.map((type) => (
            <Section
              key={type}
              type={type}
              resumeData={resumeData}
              handlers={handlers}
              config={config}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ResumeForm;
